import React, { useState } from 'react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useScreenType } from '../../hooks/useScreenType';
import './Pwa.css';

const DISMISS_KEY = 'kanea-install-dismissed-at';
const DISMISS_DURATION_DAYS = 14;

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_DURATION_DAYS * 24 * 3600 * 1000;
  } catch {
    return false;
  }
}

/**
 * Bannière d'installation PWA (mobile uniquement).
 * Android/Chrome : bouton « Installer » → prompt natif en 1 clic.
 * iOS : bouton « Comment faire ? » → guide « Ajouter à l'écran d'accueil ».
 */
const InstallPrompt: React.FC = () => {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const { isMobile } = useScreenType();
  const [dismissed, setDismissed] = useState(isDismissed());
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const canShow = isMobile && !isInstalled && !dismissed && (isInstallable || isIOS);
  if (!canShow) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* stockage indisponible (navigation privée) : la bannière reviendra */
    }
    setDismissed(true);
    setShowIOSGuide(false);
  };

  const handleInstall = async () => {
    if (isInstallable) {
      const accepted = await promptInstall();
      if (accepted) setDismissed(true);
    } else if (isIOS) {
      setShowIOSGuide(true);
    }
  };

  return (
    <>
      <div className='pwa-install-banner' role='dialog' aria-label="Installer l'application">
        <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt='' className='pwa-install-logo' />
        <div className='pwa-install-text'>
          <strong>Installez KaneaBourse</strong>
          <span>Accès instantané depuis votre écran d'accueil</span>
        </div>
        <button type='button' className='pwa-install-btn' onClick={handleInstall}>
          {isInstallable ? 'Installer' : 'Voir comment'}
        </button>
        <button type='button' className='pwa-install-close' onClick={dismiss} aria-label='Fermer'>
          <i className='material-icons'>close</i>
        </button>
      </div>

      {showIOSGuide && (
        <div className='pwa-ios-overlay' onClick={() => setShowIOSGuide(false)}>
          <div className='pwa-ios-guide' onClick={(e) => e.stopPropagation()}>
            <h4>Installer sur iPhone / iPad</h4>
            <ol>
              <li>
                Ouvrez cette page dans <strong>Safari</strong> (recommandé) ou Chrome.
              </li>
              <li>
                Touchez le bouton <strong>Partager</strong>{' '}
                <i className='material-icons pwa-ios-icon'>ios_share</i> en bas de l'écran.
              </li>
              <li>
                Choisissez <strong>« Sur l'écran d'accueil »</strong>{' '}
                <i className='material-icons pwa-ios-icon'>add_box</i>.
              </li>
              <li>
                Confirmez avec <strong>Ajouter</strong> : KaneaBourse apparaît comme une app.
              </li>
            </ol>
            <button type='button' className='pwa-install-btn' onClick={dismiss}>
              Compris
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
